/**
 * Supabase configuration resolution + transport guard
 *
 * Módulo PURO (sin efectos de import: no crea el client, no toca `window`),
 * separado de `services/supabase.js` a propósito para que el contrato de la
 * guarda se pueda testear sin instanciar un cliente real.
 *
 * ## La propiedad de seguridad que esto protege
 *
 * **Nunca hablar en `http://` con un endpoint de Supabase REAL.** Un `http://`
 * contra un host remoto viaja en claro: el anon key, el JWT de sesión y todo
 * el tráfico de PostgREST quedan expuestos a cualquiera en el camino.
 *
 * Lo que garantiza esa propiedad es el chequeo de **HOST**, no el entorno:
 * `localhost` / `127.0.0.1` no salen de la máquina, así que ahí `http://` no
 * expone nada. Hasta #698 la excepción estaba recortada por
 * `NODE_ENV === 'test'`, que es un acople accidental al runner de tests: la
 * misma URL inofensiva (`http://127.0.0.1:54321`) era aceptada bajo `test` y
 * rechazada bajo `development`, aunque el riesgo es idéntico (cero) en los dos
 * casos. El resultado fue que el stack local sólo era alcanzable desde los
 * tests y nunca desde el navegador — justo donde vive la UI que hay que mirar.
 *
 * El recorte de hoy tiene dos condiciones, y ninguna es el runner:
 *
 *  1. **Host loopback** (`localhost` / `127.0.0.1`, con puerto opcional).
 *     Cualquier otro host en `http://` tira, en CUALQUIER entorno — incluido
 *     `development`. Sin esta mitad la excepción no valdría nada: alcanzaría
 *     con correr en dev para poder apuntar a un `http://` real.
 *  2. **No producción.** `NODE_ENV === 'production'` o
 *     `REACT_APP_ENV === 'production'` exigen `https://` sin excepción, ni
 *     siquiera contra loopback. Un build de producción no tiene ningún motivo
 *     legítimo para hablarle a un Supabase local, así que ahí el permiso sólo
 *     podría venir de un error de configuración.
 *
 * O sea: el permiso se amplía de `test` a `test + development` (todo lo que NO
 * es producción) mientras la condición que de verdad sostiene la propiedad —el
 * host— queda intacta.
 *
 * @module services/supabaseConfig
 */

/** Hosts a los que se permite hablar en `http://` fuera de producción. */
const LOOPBACK_HTTP = /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/;

const DOCS_HINT =
  'Definí las variables en .env.development.local (gitignoreado). ' +
  'Plantilla: .env.example — entorno local: docs/ENTORNO_DEV_LOCAL.md';

/**
 * Error de configuración con campos estructurados, para que la pantalla de
 * error (ver el handler en `public/index.html`) pueda mostrar QUÉ variable
 * falló, QUÉ valor recibió y QUÉ se esperaba, en vez de un spinner infinito.
 */
export class SupabaseConfigError extends Error {
  constructor(message, { variable, received, expected, hint } = {}) {
    super(message);
    this.name = 'SupabaseConfigError';
    // Marca estable que el handler de `public/index.html` busca. No dependemos
    // de `instanceof` porque ahí corre JS plano fuera del bundle.
    this.isSupabaseConfigError = true;
    this.variable = variable;
    this.received = received;
    this.expected = expected;
    this.hint = hint || DOCS_HINT;
  }
}

/**
 * ¿Este entorno es producción?
 * @param {Record<string, string|undefined>} env
 * @returns {boolean}
 */
export const isProductionEnv = (env) =>
  env.NODE_ENV === 'production' || env.REACT_APP_ENV === 'production';

/**
 * ¿Esta URL apunta a un loopback por http:// (o sea, tráfico que nunca sale
 * de la máquina)?
 * @param {string} url
 * @returns {boolean}
 */
export const isLoopbackHttpUrl = (url) => LOOPBACK_HTTP.test(url || '');

/**
 * Resuelve y valida la configuración de Supabase.
 *
 * @param {Record<string, string|undefined>} [env=process.env]
 * @returns {{ url: string, anonKey: string }}
 * @throws {SupabaseConfigError} si falta una variable o la URL viola la guarda.
 */
export function resolveSupabaseConfig(env = process.env) {
  const url = env.REACT_APP_SUPABASE_URL;
  const anonKey = env.REACT_APP_SUPABASE_ANON_KEY;

  // 1. Presencia. El mensaje nombra la variable: un "configuration is required"
  //    genérico obliga a leer el código para saber qué setear.
  const missing = [];
  if (!url) missing.push('REACT_APP_SUPABASE_URL');
  if (!anonKey) missing.push('REACT_APP_SUPABASE_ANON_KEY');
  if (missing.length > 0) {
    throw new SupabaseConfigError(
      `Falta configuración de Supabase: ${missing.join(' y ')} sin definir.`,
      {
        variable: missing.join(', '),
        received: '(sin definir)',
        expected:
          'REACT_APP_SUPABASE_URL=https://<proyecto>.supabase.co (o ' +
          'http://127.0.0.1:54321 contra el stack local) + su anon key',
      }
    );
  }

  // 2. Transporte. https:// siempre pasa; http:// sólo contra loopback y sólo
  //    fuera de producción.
  if (!url.startsWith('https://')) {
    const production = isProductionEnv(env);

    if (!isLoopbackHttpUrl(url)) {
      throw new SupabaseConfigError(
        'REACT_APP_SUPABASE_URL debe usar https://. Sólo se acepta http:// ' +
          'contra localhost/127.0.0.1, y sólo fuera de producción.',
        {
          variable: 'REACT_APP_SUPABASE_URL',
          received: url,
          expected:
            'https://<host> — o http://localhost:<puerto> / ' +
            'http://127.0.0.1:<puerto> si apuntás al stack local',
        }
      );
    }

    if (production) {
      throw new SupabaseConfigError(
        'En producción REACT_APP_SUPABASE_URL debe usar https:// sin ' +
          'excepción: un build productivo no habla con un Supabase local.',
        {
          variable: 'REACT_APP_SUPABASE_URL',
          received: url,
          expected: 'https://<proyecto>.supabase.co',
        }
      );
    }
  }

  return { url, anonKey };
}

export default resolveSupabaseConfig;
